/**
 * DeliveryRoutesPage — `/delivery/routes` paginated list of routes plus an
 * interactive map preview.
 *
 * Wires `GET /v1/routes` (operator-scoped). When a row is selected we fetch
 * `GET /v1/routes/:id` and render its stops as markers + a polyline.
 *
 * The per-route polyline is driven by `RouteStopResponseDto.lat / .lng`
 * (added 2026-05-12). Stops without coordinates fall through to a
 * "Map data unavailable" notice — there is no fake sample path anymore.
 */
import { type JSX, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { getRoute, listRoutes, type RouteStatus } from '../../lib/api/delivery';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';
import { UpmpMap, type UpmpMapMarker, type UpmpMapPolyline } from '../shared/UpmpMap';
import { NIGERIA_CENTER } from '../franchise/state-centroids';
import { AutoBuildModal } from './AutoBuildModal';

const STATUS_OPTIONS: readonly (RouteStatus | '')[] = [
  '',
  'DRAFT',
  'DISPATCHED',
  'IN_PROGRESS',
  'COMPLETED',
  'RECONCILED',
];

export function DeliveryRoutesPage(): JSX.Element {
  const [status, setStatus] = useState<RouteStatus | ''>('');
  const [driverId, setDriverId] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [autoBuildOpen, setAutoBuildOpen] = useState<boolean>(false);

  const routes = useQuery({
    queryKey: ['delivery', 'routes', 'list', { status, driverId, page }],
    queryFn: () =>
      listRoutes({
        ...(status !== '' && { status }),
        ...(driverId.trim() !== '' && { driverId: driverId.trim() }),
        page,
        pageSize: 20,
      }),
    retry: false,
    staleTime: 15_000,
  });

  const denied =
    routes.isError && routes.error instanceof ApiError && (routes.error.status === 401 || routes.error.status === 403);

  const total = routes.data?.total ?? 0;
  const pageSize = routes.data?.pageSize ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Fetch the selected route's stops (with lat/lng) for the map preview.
  const selectedRoute = useQuery({
    queryKey: ['delivery', 'routes', 'detail', selectedRouteId],
    queryFn: () => getRoute(selectedRouteId as string),
    retry: false,
    enabled: selectedRouteId !== null,
    staleTime: 30_000,
  });

  // Stops that carry usable coordinates — strict parse so malformed strings
  // never reach the map and crash it. Drops null + NaN values silently.
  const geoStops = useMemo<
    readonly { readonly id: string; readonly sequence: number; readonly lat: number; readonly lng: number }[]
  >(() => {
    const stops = selectedRoute.data?.stops ?? [];
    const out: { id: string; sequence: number; lat: number; lng: number }[] = [];
    for (const s of stops) {
      if (s.lat == null || s.lng == null) continue;
      const lat = Number.parseFloat(s.lat);
      const lng = Number.parseFloat(s.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      out.push({ id: s.id, sequence: s.sequence, lat, lng });
    }
    return out;
  }, [selectedRoute.data]);

  const markers = useMemo<readonly UpmpMapMarker[]>(
    () =>
      geoStops.map((s) => ({
        id: s.id,
        position: { lat: s.lat, lng: s.lng },
        title: `Stop ${s.sequence}`,
        label: String(s.sequence),
      })),
    [geoStops],
  );

  const polylines = useMemo<readonly UpmpMapPolyline[]>(
    () =>
      geoStops.length >= 2
        ? [
            {
              id: 'route-path',
              path: geoStops.map((s) => ({ lat: s.lat, lng: s.lng })),
            },
          ]
        : [],
    [geoStops],
  );

  // Centre the map on the first stop with coordinates; fall back to the
  // country centroid when nothing is selected or no coords are available.
  const mapCenter = geoStops[0]
    ? { lat: geoStops[0].lat, lng: geoStops[0].lng }
    : NIGERIA_CENTER;
  const mapZoom = geoStops.length > 0 ? 10 : 6;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Routes"
        description="Operator-scoped delivery routes."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAutoBuildOpen(true)}
              className="inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Build today&apos;s routes
            </button>
            <Link
              to="/delivery/routes/new"
              className="inline-flex items-center px-3 py-2 bg-nipost-green text-white rounded-lg text-sm font-semibold"
            >
              New route
            </Link>
          </div>
        }
      />

      {autoBuildOpen ? <AutoBuildModal onClose={() => setAutoBuildOpen(false)} /> : null}

      <FilterBar
        searchValue={driverId}
        onSearchChange={(v) => {
          setDriverId(v);
          setPage(1);
        }}
        searchPlaceholder="Filter by driver id…"
      >
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as RouteStatus | '');
            setPage(1);
          }}
          aria-label="Status filter"
          className="px-3 py-2 text-sm border border-border rounded-lg bg-white"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s || 'all'} value={s}>
              {s === '' ? 'All statuses' : s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </FilterBar>

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Map preview</h3>
            <p className="text-[11px] text-muted mt-0.5">
              {selectedRouteId !== null
                ? selectedRoute.isPending
                  ? 'Loading stops…'
                  : `Route ${selectedRoute.data?.routeCode ?? selectedRouteId.slice(0, 8) + '…'} · ${geoStops.length} of ${selectedRoute.data?.stops?.length ?? 0} stops geocoded`
                : 'Click a row below to focus a route.'}
            </p>
          </div>
        </div>
        {selectedRouteId !== null && !selectedRoute.isPending && geoStops.length === 0 ? (
          <div className="p-3">
            <DeferredNotice title="Map data unavailable for this route">
              <p>
                None of this route&apos;s stops carry geo coordinates yet —
                upstream addresses booked without lat/lng cannot be plotted.
                Stops captured after the booking flow attaches geo will appear
                on the map automatically.
              </p>
            </DeferredNotice>
          </div>
        ) : null}
        <UpmpMap
          center={mapCenter}
          zoom={mapZoom}
          markers={markers}
          polylines={polylines}
          height={360}
        />
      </div>

      {routes.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Delivery/readRoute</code>.
        </DeferredNotice>
      ) : routes.isError ? (
        <DeferredNotice title="Service unreachable">
          {(routes.error as Error).message}
        </DeferredNotice>
      ) : (
        <>
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Driver</th>
                  <th className="text-left px-4 py-2">Vehicle</th>
                  <th className="text-left px-4 py-2">Stops</th>
                  <th className="text-left px-4 py-2">Scheduled</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(routes.data?.items ?? []).map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setSelectedRouteId(r.id)}
                    className={`cursor-pointer ${selectedRouteId === r.id ? 'bg-nipost-green/10' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-4 py-2 font-mono text-xs font-bold text-gray-900">{r.routeCode}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 text-xs text-muted">{r.driverId ?? '—'}</td>
                    <td className="px-4 py-2 text-xs text-muted">{r.vehicleId ?? '—'}</td>
                    <td className="px-4 py-2 text-xs text-muted">{r.stopCount}</td>
                    <td className="px-4 py-2 text-xs text-muted">{new Date(r.scheduledStart).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to={`/delivery/items/${encodeURIComponent(r.id)}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs font-semibold text-nipost-green hover:underline"
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(routes.data?.items.length ?? 0) === 0 ? (
              <p className="p-8 text-center text-sm text-muted">No routes for the current filters.</p>
            ) : null}
          </div>
          <div className="flex items-center justify-between mt-4 text-xs text-muted">
            <span>Page {page} of {totalPages} · {total} routes</span>
            <div className="flex gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50">Previous</button>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50">Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
